import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { from, minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalAlert,
  MinimalBadge,
  MinimalButton,
  MinimalEmptyState,
  MinimalInput,
} from '@ovasabi/ui-minimal'
import {
  ConversationPanel,
  ListSkeleton,
  Money,
  PageHeader,
  Screen,
  StatusBadge,
  type ConversationMessage,
} from '../../components/chow'
import { ArrowLeftIcon, ChatIcon, SearchIcon, ChefHatIcon, UserIcon } from '../../components/icons'
import {
  useMarketplaceOrderSnapshot,
  useOrderMessageSnapshot,
  useOrderThreadSnapshot,
} from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { useLiveRuntimePending, useProjectionReady, useScopeRevision } from '../../lib/projectionHealth'
import { confirmedUpsert, enumLabel, enumShort, getStore, type StoreRecord } from './chefShared'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { isUnpaidMealPlanHold, orderReference, orderSummary, useOrderItemNames } from './orderDisplay'
import { useKitchenNames } from './projections'
import { markThreadRead, useUnreadOrdersMap } from './unreadMessages'
import { compareTimestamps, formatRFC3339UTC, isAfterTimestamp } from '@ovasabi/frontend-kit'

/** Unread tint, mixed off the `danger` token. */
const UNREAD_TINT = `color-mix(in srgb, ${minimalVars.color.danger} 4%, transparent)`

const Style = {
  HeaderWrap: styled.div<{ $hideOnMobile?: boolean }>`
    /* 767px matches no named stop. It sits just under the page stop on purpose. */
    @media (max-width: 767px) {
      display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'block')};
    }
  `,
  Layout: styled.div`
    display: flex;
    gap: ${minimalVars.space.sm};
    align-items: stretch;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    width: 100%;

    /* 767px matches no named stop. Keeps the phone split off the page stop. */
    @media (max-width: 767px) {
      flex-direction: column;
      gap: 0;
    }
  `,
  Sidebar: styled.aside<{ $hiddenOnMobile: boolean }>`
    width: clamp(240px, 30vw, 350px);
    min-width: 0;
    flex-shrink: 0;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.md};
    overflow: hidden;
    box-shadow: ${minimalVars.shadow.subtle};

    /* 767px matches no named stop. Keeps the phone split off the page stop. */
    @media (max-width: 767px) {
      width: 100%;
      height: 100%;
      border-radius: 0;
      border: none;
      box-shadow: none;
      display: ${({ $hiddenOnMobile }) => ($hiddenOnMobile ? 'none' : 'flex')};
    }
  `,
  SearchBox: styled.div`
    padding: 12px;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    position: relative;
    display: flex;
    align-items: center;
  `,
  ThreadList: styled.div`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    overscroll-behavior: contain;
  `,
  ThreadCard: styled.button<{ $active: boolean; $unread: boolean }>`
    text-align: left;
    background: ${({ $active, $unread }) =>
      $active
        ? minimalVars.color.brandSoft
        : $unread
        ? UNREAD_TINT
        : 'transparent'};
    border: none;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    border-left: 4px solid
      ${({ $active, $unread }) =>
        $active
          ? minimalVars.color.brand
          : $unread
          ? minimalVars.color.brand
          : 'transparent'};
    padding: 14px ${minimalVars.space.sm};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    gap: 6px;
    transition: background-color 140ms ease;

    &:hover {
      background: ${({ $active }) =>
        $active ? minimalVars.color.brandSoft : minimalVars.color.bgSurfaceHover};
    }
  `,
  ThreadHeader: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  ThreadRef: styled.span<{ $unread: boolean }>`
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${({ $unread }) => ($unread ? 800 : 600)};
    color: ${({ $unread }) => ($unread ? minimalVars.color.brand : minimalVars.color.textPrimary)};
  `,
  /* Kept below metaSize on purpose. A timestamp badge must stay one line. */
  ThreadTime: styled.span`
    font-size: 11px;
    color: ${minimalVars.color.textTertiary};
  `,
  ThreadParty: styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    > svg { flex-shrink: 0; }
    > span { min-width: 0; overflow-wrap: anywhere; }
    font-size: ${minimalVars.typography.bodySize};
    font-weight: 700;
    color: ${minimalVars.color.textPrimary};
  `,
  ThreadPreviewRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  ThreadPreview: styled.span<{ $unread: boolean }>`
    font-size: ${minimalVars.typography.captionSize};
    color: ${({ $unread }) =>
      $unread ? minimalVars.color.textPrimary : minimalVars.color.textTertiary};
    font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
  `,
  ThreadSubRow: styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: ${minimalVars.color.textTertiary};
    overflow: hidden;
  `,
  ThreadSubText: styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  /* Kept below metaSize on purpose. The count must fit its pill on one line. */
  UnreadBadge: styled.span`
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-size: 10px;
    font-weight: 800;
    padding: 1px 7px;
    border-radius: ${minimalVars.radius.pill};
    line-height: 1.3;
    flex-shrink: 0;
  `,
  ChatPane: styled.main<{ $hiddenOnMobile: boolean }>`
    flex: 1;
    min-width: 0;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.md};
    overflow: hidden;
    box-shadow: ${minimalVars.shadow.subtle};

    /* 767px matches no named stop. Keeps the phone split off the page stop. */
    @media (max-width: 767px) {
      width: 100%;
      height: 100%;
      border-radius: 0;
      border: none;
      box-shadow: none;
      display: ${({ $hiddenOnMobile }) => ($hiddenOnMobile ? 'none' : 'flex')};
    }
  `,
  ChatHeader: styled.div`
    padding: 12px ${minimalVars.space.sm};
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    min-width: 0;
    background: ${minimalVars.color.bgSurfaceAlt};
    flex-shrink: 0;
  `,
  ChatHeaderLeft: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1 1 200px;
  `,
  BackButton: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid ${minimalVars.color.borderStrong};
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textPrimary};
    cursor: pointer;
    flex-shrink: 0;

    ${from('page')} {
      display: none;
    }
  `,
  ChatHeaderInfo: styled.div`
    display: grid;
    gap: 2px;
    min-width: 0;
  `,
  ChatTitle: styled.h3`
    margin: 0;
    font-size: ${minimalVars.typography.bodySize};
    font-weight: 700;
    color: ${minimalVars.color.textPrimary};
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    flex-wrap: nowrap;
    min-width: 0;
  `,
  ChatName: styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  ChatSub: styled.span`
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textSecondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  ChatHeaderRight: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    max-width: 100%;
    margin-left: auto;
  `,
  OrderContextBar: styled.div`
    padding: 8px 12px;
    background: ${minimalVars.color.bgSurfaceAlt};
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    flex-shrink: 0;
  `,
  OrderContextLabel: styled.span`
    font-size: 11px;
    font-weight: 700;
    color: ${minimalVars.color.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  `,
  OrderChip: styled.button<{ $active: boolean }>`
    background: ${({ $active }) =>
      $active ? minimalVars.color.brand : minimalVars.color.bgSurface};
    color: ${({ $active }) =>
      $active ? minimalVars.color.textInverse : minimalVars.color.textPrimary};
    border: 1px solid
      ${({ $active }) =>
        $active ? minimalVars.color.brand : minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.pill};
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: border-color 140ms ease, background-color 140ms ease, color 140ms ease;

    &:hover {
      border-color: ${minimalVars.color.brand};
    }
  `,
  SuggestionBar: styled.div`
    padding: ${minimalVars.space.xs} 14px;
    background: ${minimalVars.color.bgSurfaceAlt};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
    display: flex;
    gap: ${minimalVars.space.xs};
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    flex-shrink: 0;

    &::-webkit-scrollbar {
      display: none;
    }
  `,
  SuggestionChip: styled.button`
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.pill};
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: 600;
    padding: 6px 14px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    transition: border-color 140ms ease, background-color 140ms ease, color 140ms ease, transform 140ms ease;

    &:hover {
      border-color: ${minimalVars.color.brand};
      color: ${minimalVars.color.brand};
      background: ${minimalVars.color.brandSoft};
      transform: translateY(-1px);
    }
    &:active {
      transform: scale(0.97);
    }
  `,
  PanelWrap: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 12px;
  `,
}

const asConversationMessage = (message: Record<string, unknown>): ConversationMessage => {
  const senderClass = String(message.sender_class ?? '')
  const metadata =
    message.metadata && typeof message.metadata === 'object'
      ? (message.metadata as Record<string, unknown>)
      : {}
  const systemGenerated = metadata.system_generated === true
  let senderLabel = 'Customer'
  if (systemGenerated) senderLabel = 'ChooseChow'
  else if (senderClass === 'chef') senderLabel = 'Chef'
  else if (senderClass === 'rider') senderLabel = 'Rider'
  return {
    id: String(message.id),
    body: String(message.body ?? ''),
    senderId: String(message.sender_profile_id ?? ''),
    senderLabel,
    createdAt: formatRFC3339UTC(message.created_at) || undefined,
  }
}

const formatPreviewTime = (dateStr?: string): string => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const CHEF_SUGGESTIONS = [
  'Order received! Cooking now 🍲',
  'Almost ready for pickup ⏱️',
  'Packed and handed to delivery 🛵',
  'Any allergies or instructions? 📝',
  'Thank you for your order! 🙏',
]

const CUSTOMER_SUGGESTIONS = [
  'Hi chef! How long will it take? ⏱️',
  'Please do not make it too spicy 🌶️',
  'Please add extra pepper/sauce 🍲',
  'I am at the gate / door 🚪',
  'Thank you so much! 🙌',
]

export interface ChefConversation {
  partyId: string
  partyName: string
  orders: readonly Record<string, unknown>[]
  latestOrder: Record<string, unknown>
  unreadCount: number
  lastMessage?: string
  lastCreatedAt?: string
}

export const InboxPage = () => {
  const navigate = useNavigate()
  const back = useBackNav()
  const { role, profileId, isChef } = useChooseChow()
  const orders = useMarketplaceOrderSnapshot() as { records: readonly Record<string, unknown>[] }
  const threads = useOrderThreadSnapshot() as { records: readonly Record<string, unknown>[] }
  const messageSnapshot = useOrderMessageSnapshot() as { records: readonly Record<string, unknown>[] }
  const kitchenNames = useKitchenNames()
  const unreadMap = useUnreadOrdersMap(profileId, role)

  const ordersReady = useProjectionReady('marketplace', 'marketplace_orders')
  // Rows read before the live runtime is swapped in are the offline runtime's
  // placeholders, not this user's chats — never draw them as conversations.
  const runtimePending = useLiveRuntimePending()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedOrderId = searchParams.get('order') ?? ''
  // A kitchen is addressed by its profile id; the name is only a fallback for
  // older links (?chef=<name>), since two kitchens can share a display name.
  const requestedKitchenId = (searchParams.get('kitchen') ?? '').toLowerCase()
  const requestedKitchenName = searchParams.get('name') ?? searchParams.get('chef') ?? ''
  const [selectedPartyId, setSelectedPartyId] = useState('')
  const [selectedOrderId, setSelectedOrderId] = useState(requestedOrderId)
  // Opened only once a requested thread is found. Opening it up front left a
  // phone on an empty chat pane when the customer had no order with the kitchen.
  const [mobileChatOpen, setMobileChatOpen] = useState(Boolean(requestedOrderId))
  const [searchQuery, setSearchQuery] = useState('')

  // Orders scoped to the current user, strictly excluding unconfirmed calendar holds
  const myOrders = useMemo(() => {
    let list = orders.records.filter((order) => !isUnpaidMealPlanHold(order))
    if (isLiveRuntime()) {
      list = list.filter((order) => {
        const id = String(order.id ?? '')
        if (id.startsWith('order-') || id.includes('-prototype')) return false
        if (role === 'chef') {
          return String(order.merchant_profile_id ?? '') === profileId
        } else {
          return String(order.customer_profile_id ?? '') === profileId
        }
      })
    }
    return [...list].sort((a, b) => {
      const idA = String(a.id ?? '')
      const idB = String(b.id ?? '')
      const timeA = unreadMap[idA]?.lastCreatedAt || a.created_at
      const timeB = unreadMap[idB]?.lastCreatedAt || b.created_at
      return compareTimestamps(timeA, timeB, idA, idB, { direction: 'desc' })
    })
  }, [orders.records, role, profileId, unreadMap])

  const myThreads = useMemo(() => {
    let list = threads.records
    if (isLiveRuntime()) {
      list = list.filter((t) => {
        const id = String(t.id ?? '')
        return !id.startsWith('thread-') && !id.includes('-prototype')
      })
    }
    return list
  }, [threads.records])

  const myMessages = useMemo(() => {
    let list = messageSnapshot.records
    if (isLiveRuntime()) {
      list = list.filter((m) => {
        const id = String(m.id ?? '')
        return !id.startsWith('message-') && !id.includes('-prototype')
      })
    }
    return list
  }, [messageSnapshot.records])

  const orderItemNames = useOrderItemNames(myOrders.map((order) => String(order.id ?? '')))

  // Group conversations by Chef/Kitchen (for customers) or Customer (for chefs)
  const conversations = useMemo<ChefConversation[]>(() => {
    const partyMap = new Map<string, Record<string, unknown>[]>()
    for (const order of myOrders) {
      const partyId = String(isChef ? order.customer_profile_id : order.merchant_profile_id)
      if (!partyId) continue
      const existing = partyMap.get(partyId) ?? []
      existing.push(order)
      partyMap.set(partyId, existing)
    }

    const list: ChefConversation[] = []
    for (const [partyId, groupOrders] of partyMap.entries()) {
      const sortedOrders = [...groupOrders].sort((a, b) => {
        const idA = String(a.id ?? '')
        const idB = String(b.id ?? '')
        const timeA = unreadMap[idA]?.lastCreatedAt || a.created_at
        const timeB = unreadMap[idB]?.lastCreatedAt || b.created_at
        return compareTimestamps(timeA, timeB, idA, idB, { direction: 'desc' })
      })
      const latestOrder = sortedOrders[0]
      const partyName = isChef
        ? `Customer #${partyId.slice(-4)}`
        : kitchenNames(partyId) || 'Kitchen'

      let unreadCount = 0
      let lastMessage = ''
      let lastCreatedAt = ''
      for (const order of sortedOrders) {
        const id = String(order.id ?? '')
        const info = unreadMap[id]
        if (info) {
          unreadCount += info.unreadCount ?? 0
          if (info.lastCreatedAt && (!lastCreatedAt || isAfterTimestamp(info.lastCreatedAt, lastCreatedAt))) {
            lastCreatedAt = info.lastCreatedAt
            lastMessage = info.lastMessage ?? ''
          }
        }
      }
      if (!lastMessage && latestOrder) {
        lastMessage = orderSummary(latestOrder, orderItemNames[String(latestOrder.id ?? '')])
      }
      if (!lastCreatedAt && latestOrder) {
        lastCreatedAt = String(latestOrder.created_at ?? '')
      }

      list.push({
        partyId,
        partyName,
        orders: sortedOrders,
        latestOrder,
        unreadCount,
        lastMessage,
        lastCreatedAt,
      })
    }

    return list.sort((a, b) =>
      compareTimestamps(a.lastCreatedAt, b.lastCreatedAt, a.partyId, b.partyId, { direction: 'desc' })
    )
  }, [myOrders, isChef, kitchenNames, unreadMap, orderItemNames])

  // Resolve selection from query parameters (?order= or ?chef=)
  useEffect(() => {
    if (requestedOrderId) {
      const matched = myOrders.find((o) => String(o.id) === requestedOrderId)
      if (matched) {
        const party = String(isChef ? matched.customer_profile_id : matched.merchant_profile_id)
        setSelectedPartyId(party)
        setSelectedOrderId(requestedOrderId)
        setMobileChatOpen(true)
        return
      }
    }
    if (requestedKitchenId || requestedKitchenName) {
      const matched = conversations.find(
        (c) => (requestedKitchenId && c.partyId.toLowerCase() === requestedKitchenId) ||
          (requestedKitchenName && c.partyName.trim().toLowerCase() === requestedKitchenName.trim().toLowerCase())
      )
      if (matched) {
        setSelectedPartyId(matched.partyId)
        setSelectedOrderId(String(matched.latestOrder?.id ?? ''))
        setMobileChatOpen(true)
      }
    }
  }, [requestedOrderId, requestedKitchenId, requestedKitchenName, myOrders, conversations, isChef])

  // Chat is attached to orders, so a kitchen the customer has never ordered
  // from has no thread yet. Say so instead of opening someone else's chat.
  const missingKitchenThread = ordersReady && !isChef && !requestedOrderId && Boolean(requestedKitchenId || requestedKitchenName) &&
    !conversations.some((c) => (requestedKitchenId && c.partyId.toLowerCase() === requestedKitchenId) ||
      (requestedKitchenName && c.partyName.trim().toLowerCase() === requestedKitchenName.trim().toLowerCase()))
  const missingKitchenLabel = requestedKitchenName || 'this kitchen'

  // Pin default selection to first conversation
  useEffect(() => {
    if (selectedPartyId) return
    const first = conversations[0]
    if (first) {
      setSelectedPartyId(first.partyId)
      setSelectedOrderId(String(first.latestOrder?.id ?? ''))
    }
  }, [selectedPartyId, conversations])

  const selectedConversation = conversations.find((c) => c.partyId === selectedPartyId) ?? conversations[0]
  const activeOrder =
    selectedConversation?.orders.find((order) => String(order.id) === selectedOrderId) ??
    selectedConversation?.latestOrder

  const activeOrderId = activeOrder ? String(activeOrder.id) : ''
  const activeOrderIds = useMemo(
    () => (selectedConversation ? selectedConversation.orders.map((o) => String(o.id ?? '')) : []),
    [selectedConversation]
  )

  const activeThreads = useMemo(
    () => myThreads.filter((item) => activeOrderIds.includes(String(item.order_id ?? ''))),
    [myThreads, activeOrderIds]
  )
  const activeThreadIds = useMemo(() => activeThreads.map((t) => String(t.id)), [activeThreads])

  const currentThread = activeThreads.find((item) => String(item.order_id ?? '') === activeOrderId)
  const projectedThreadId = currentThread ? String(currentThread.id) : ''
  const [resolvedThreadId, setResolvedThreadId] = useState('')
  const threadId = projectedThreadId || resolvedThreadId
  const threadSubject = activeOrder
    ? `Order ${String(activeOrder.reference ?? activeOrderId)}`
    : ''

  useEffect(() => {
    setResolvedThreadId('')
  }, [activeOrderId])

  // Start order thread if not yet created for the currently active order
  useEffect(() => {
    if (!isLiveRuntime() || !activeOrderId || projectedThreadId) return undefined
    let alive = true
    void commands.message
      .startOrderThread({
        order_id: activeOrderId,
        participant_profile_id: profileId,
        subject: threadSubject,
      })
      .then((res) => {
        const row = commandPayload(res)
        const id = String(row.id ?? '')
        if (!alive || !id) return
        confirmedUpsert('message.order_threads', row)
        setResolvedThreadId(id)
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [activeOrderId, projectedThreadId, profileId, threadSubject])

  // Fetch transcripts for threads in this conversation without infinite loop
  const messageStreamRevision = useScopeRevision('message', 'order_messages')
  const lastFetchedRevisionRef = useRef<number>(-1)
  useEffect(() => {
    if (!isLiveRuntime() || activeThreadIds.length === 0) return undefined
    let alive = true
    lastFetchedRevisionRef.current = messageStreamRevision
    for (const tId of activeThreadIds) {
      if (!tId) continue
      void commands.message
        .listOrderMessages(tId)
        .then((res) => {
          if (!alive) return
          const rows = commandPayload<{ messages?: Record<string, unknown>[] }>(res).messages ?? []
          for (const row of rows) {
            if (String(row.id ?? '')) confirmedUpsert('message.order_messages', row)
          }
        })
        .catch(() => undefined)
    }
    return () => {
      alive = false
    }
  }, [activeThreadIds.join(','), messageStreamRevision])

  // Unified messages across all orders for this chef/customer
  const shownMessages = useMemo(() => {
    const threadSet = new Set(activeThreadIds)
    if (threadId) threadSet.add(threadId)
    const orderSet = new Set(activeOrderIds)

    return myMessages
      .filter((message) => {
        const tId = String(message.thread_id ?? '')
        const oId = String(message.order_id ?? '')
        return (tId && threadSet.has(tId)) || (oId && orderSet.has(oId))
      })
      .slice()
      .sort((a, b) => compareTimestamps(a.created_at, b.created_at, String(a.id ?? ''), String(b.id ?? '')))
  }, [myMessages, activeThreadIds, threadId, activeOrderIds])
  const messages = useMemo<ConversationMessage[]>(() => shownMessages.map(asConversationMessage), [shownMessages])

  // Mark every thread on screen read up to the newest message shown.
  //
  // The mark is the RAW created_at, never the formatted one: Postgres stamps
  // microseconds and toISOString keeps milliseconds, so a mark taken from the
  // formatted time always sat just before the message it was meant to cover
  // and the unread badge never cleared. Marking each message's own thread also
  // covers a conversation that reached the screen through its order id.
  useEffect(() => {
    if (shownMessages.length === 0) return
    const newestRaw = String(shownMessages[shownMessages.length - 1].created_at ?? '')
    const newestByThread = new Map<string, string>()
    for (const message of shownMessages) {
      const tId = String(message.thread_id ?? '')
      if (tId) newestByThread.set(tId, String(message.created_at ?? ''))
    }
    for (const tId of [...activeThreadIds, threadId]) {
      if (tId && !newestByThread.has(tId)) newestByThread.set(tId, newestRaw)
    }
    for (const [tId, at] of newestByThread) markThreadRead(tId, at)
  }, [activeThreadIds, threadId, shownMessages])

  const echoMessage = (activeThreadId: string, body: string) => {
    if (!activeOrder) return
    confirmedUpsert('message.order_messages', {
      id: `message-${Math.random().toString(36).slice(2)}`,
      organization_id: String(activeOrder.organization_id ?? 'org-prototype'),
      thread_id: activeThreadId,
      order_id: activeOrderId,
      sender_profile_id: profileId,
      sender_class: role,
      body,
      created_at: new Date().toISOString(),
    })
  }

  const sendMessage = async (body: string) => {
    if (!activeOrder) return
    if (threadId) {
      if (isLiveRuntime()) {
        const sent = await commands.message.sendOrderMessage(threadId, {
          sender_profile_id: profileId,
          sender_class: role,
          body,
        })
        const row = commandPayload(sent) as StoreRecord
        if (row && row.id) confirmedUpsert('message.order_messages', row)
      } else {
        void commands.message
          .sendOrderMessage(threadId, {
            sender_profile_id: profileId,
            sender_class: role,
            body,
          })
          .catch(() => undefined)
        echoMessage(threadId, body)
      }
      return
    }

    if (isLiveRuntime()) {
      const started = await commands.message.startOrderThread({
        order_id: activeOrderId,
        participant_profile_id: profileId,
        subject: threadSubject,
      })
      const startedThread = commandPayload(started) as StoreRecord
      if (startedThread && startedThread.id) {
        confirmedUpsert('message.order_threads', startedThread)
        setResolvedThreadId(String(startedThread.id))
        const sent = await commands.message.sendOrderMessage(String(startedThread.id), {
          sender_profile_id: profileId,
          sender_class: role,
          body,
        })
        const row = commandPayload(sent) as StoreRecord
        if (row && row.id) confirmedUpsert('message.order_messages', row)
      }
      return
    }

    const localThreadId = `thread-${Math.random().toString(36).slice(2)}`
    void commands.message
      .startOrderThread({
        order_id: activeOrderId,
        participant_profile_id: String(
          role === 'chef'
            ? (activeOrder.customer_profile_id ?? 'customer-prototype')
            : (activeOrder.merchant_profile_id ?? 'merchant-prototype'),
        ),
        subject: threadSubject,
      })
      .catch(() => undefined)
    getStore('message.order_threads')?.upsert({
      id: localThreadId,
      organization_id: String(activeOrder.organization_id ?? 'org-prototype'),
      order_id: activeOrderId,
      status: 'open',
      subject: threadSubject,
      created_at: new Date().toISOString(),
    })
    echoMessage(localThreadId, body)
  }

  // Filter conversations for sidebar search
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations
    const query = searchQuery.toLowerCase()
    return conversations.filter((conv) => {
      const party = conv.partyName.toLowerCase()
      const last = (conv.lastMessage ?? '').toLowerCase()
      const matchesOrder = conv.orders.some((o) => {
        const ref = orderReference(o).toLowerCase()
        const summary = orderSummary(o, orderItemNames[String(o.id ?? '')]).toLowerCase()
        return ref.includes(query) || summary.includes(query)
      })
      return party.includes(query) || last.includes(query) || matchesOrder
    })
  }, [conversations, searchQuery, orderItemNames])

  const selectConversation = (partyId: string, orderId?: string) => {
    setSelectedPartyId(partyId)
    const targetConv = conversations.find((c) => c.partyId === partyId)
    let targetOrderId = orderId
    if (!targetOrderId && searchQuery.trim() && targetConv) {
      const q = searchQuery.toLowerCase()
      const matchingOrder = targetConv.orders.find((o) => {
        const ref = orderReference(o).toLowerCase()
        const summary = orderSummary(o, orderItemNames[String(o.id ?? '')]).toLowerCase()
        return ref.includes(q) || summary.includes(q)
      })
      if (matchingOrder) {
        targetOrderId = String(matchingOrder.id ?? '')
      }
    }
    if (!targetOrderId) {
      targetOrderId = String(targetConv?.latestOrder?.id ?? '')
    }
    setSelectedOrderId(targetOrderId)
    if (targetOrderId) setSearchParams({ order: targetOrderId })
    setMobileChatOpen(true)
  }

  const suggestions = isChef ? CHEF_SUGGESTIONS : CUSTOMER_SUGGESTIONS

  return (
    <Screen
      fill
      flushMobile
      header={
        <Style.HeaderWrap $hideOnMobile={mobileChatOpen}>
          <PageHeader
            title="Messages"
            accentDot
            onBack={back}
          />
        </Style.HeaderWrap>
      }
    >
      {runtimePending || (!ordersReady && conversations.length === 0) ? (
        <ListSkeleton rows={4} label="Loading conversations" />
      ) : conversations.length === 0 ? (
        <MinimalEmptyState
          style={{ alignContent: 'center', gap: 10, padding: '24px 16px' }}
          icon={<ChatIcon />}
          title={missingKitchenThread ? `No chat with ${missingKitchenLabel} yet` : 'Your order conversations will appear here'}
          description={
            isChef
              ? 'When someone orders from you, you can chat with them here.'
              : missingKitchenThread
                ? 'Kitchen chats open with an order. Place one and you can message the kitchen right here.'
                : 'Place your first order, then message the kitchen from this page.'
          }
          action={
            isChef ? (
              <MinimalButton onClick={() => navigate('/orders')}>View Kitchen Queue</MinimalButton>
            ) : (
              <MinimalButton onClick={() => navigate('/dishes')}>Browse dishes</MinimalButton>
            )
          }
        />
      ) : (
        <Style.Layout>
          {/* Thread List Sidebar */}
          <Style.Sidebar $hiddenOnMobile={mobileChatOpen}>
            {missingKitchenThread && (
              <MinimalAlert tone="info" title={`No chat with ${missingKitchenLabel} yet`}>
                Kitchen chats open with an order. Your other conversations are below.
              </MinimalAlert>
            )}
            <Style.SearchBox>
              <MinimalInput
                prefix={<SearchIcon size="sm" />}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders, dishes, chats…"
                aria-label="Search conversations"
              />
            </Style.SearchBox>

            <Style.ThreadList role="navigation" aria-label="Conversation threads">
              {filteredConversations.map((conv) => {
                const isSelected = conv.partyId === selectedPartyId
                const isUnread = conv.unreadCount > 0
                const timeStr = formatPreviewTime(conv.lastCreatedAt)

                return (
                  <Style.ThreadCard
                    key={conv.partyId}
                    $active={isSelected}
                    $unread={isUnread}
                    onClick={() => selectConversation(conv.partyId)}
                    aria-selected={isSelected}
                    role="button"
                  >
                    <Style.ThreadHeader>
                      <Style.ThreadParty>
                        {isChef ? <UserIcon size="sm" /> : <ChefHatIcon size="sm" />}
                        <Style.ThreadRef $unread={isUnread}>{conv.partyName}</Style.ThreadRef>
                      </Style.ThreadParty>
                      <Style.ThreadTime>{timeStr}</Style.ThreadTime>
                    </Style.ThreadHeader>

                    <Style.ThreadPreviewRow>
                      <Style.ThreadPreview $unread={isUnread}>
                        {conv.lastMessage || 'No messages yet'}
                      </Style.ThreadPreview>
                      {isUnread && <Style.UnreadBadge>{conv.unreadCount}</Style.UnreadBadge>}
                    </Style.ThreadPreviewRow>

                    {conv.orders.length > 1 ? (
                      <Style.ThreadSubRow>
                        <MinimalBadge tone="neutral" size="sm">{conv.orders.length} orders</MinimalBadge>
                        <Style.ThreadSubText>{orderReference(conv.latestOrder)}</Style.ThreadSubText>
                      </Style.ThreadSubRow>
                    ) : conv.latestOrder ? (
                      <Style.ThreadSubRow>
                        <Style.ThreadSubText>{orderReference(conv.latestOrder)}</Style.ThreadSubText>
                      </Style.ThreadSubRow>
                    ) : null}
                  </Style.ThreadCard>
                )
              })}
            </Style.ThreadList>
          </Style.Sidebar>

          {/* Active Chat Main Area */}
          <Style.ChatPane $hiddenOnMobile={!mobileChatOpen}>
            {selectedConversation && activeOrder ? (
              <>
                <Style.ChatHeader>
                  <Style.ChatHeaderLeft>
                    <Style.BackButton
                      onClick={() => setMobileChatOpen(false)}
                      aria-label="Back to conversations"
                    >
                      <ArrowLeftIcon size="sm" />
                    </Style.BackButton>
                    <Style.ChatHeaderInfo>
                      <Style.ChatTitle>
                        <Style.ChatName data-chat-name>
                          {selectedConversation.partyName}
                        </Style.ChatName>
                        <StatusBadge
                          status={
                            enumShort(activeOrder.status) === 'completed'
                              ? 'closed'
                              : 'open'
                          }
                        />
                      </Style.ChatTitle>
                      <Style.ChatSub>
                        {orderReference(activeOrder)} · {orderSummary(activeOrder, orderItemNames[activeOrderId])}
                      </Style.ChatSub>
                    </Style.ChatHeaderInfo>
                  </Style.ChatHeaderLeft>

                  <Style.ChatHeaderRight>
                    <Money
                      amount={Number(activeOrder.total_minor ?? 0) / 100}
                      tone="brand"
                      size="sm"
                    />
                    <MinimalButton
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        navigate(isChef ? `/orders/${activeOrderId}` : '/orders')
                      }
                    >
                      View Order
                    </MinimalButton>
                  </Style.ChatHeaderRight>
                </Style.ChatHeader>

                {/* Multiple Orders Context Bar */}
                {selectedConversation.orders.length > 1 && (
                  <Style.OrderContextBar aria-label="Orders with this kitchen">
                    <Style.OrderContextLabel>Orders:</Style.OrderContextLabel>
                    {selectedConversation.orders.map((ord) => {
                      const ordId = String(ord.id ?? '')
                      const isCurrent = ordId === activeOrderId
                      return (
                        <Style.OrderChip
                          key={ordId}
                          $active={isCurrent}
                          onClick={() => {
                            setSelectedOrderId(ordId)
                            setSearchParams({ order: ordId })
                          }}
                          type="button"
                        >
                          {orderReference(ord)} · {enumLabel(ord.status)}
                        </Style.OrderChip>
                      )
                    })}
                  </Style.OrderContextBar>
                )}

                {/* Quick Reply Suggestions */}
                <Style.SuggestionBar aria-label="Quick reply suggestions">
                  {suggestions.map((suggestion) => (
                    <Style.SuggestionChip
                      key={suggestion}
                      onClick={() => void sendMessage(suggestion)}
                    >
                      {suggestion}
                    </Style.SuggestionChip>
                  ))}
                </Style.SuggestionBar>

                <Style.PanelWrap>
                  <ConversationPanel
                    messages={messages}
                    currentActorId={profileId}
                    onSend={sendMessage}
                    placeholder={`Message ${
                      isChef
                        ? 'customer'
                        : selectedConversation.partyName || 'kitchen'
                    }…`}
                  />
                </Style.PanelWrap>
              </>
            ) : (
              <MinimalEmptyState
                style={{ alignContent: 'center', gap: 10, padding: '24px 16px' }}
                icon={<ChatIcon />}
                title="Select a conversation"
                description="Choose an order from the list on the left to start chatting."
              />
            )}
          </Style.ChatPane>
        </Style.Layout>
      )}
    </Screen>
  )
}
